/**
 * yd-scroll-lock — 全站统一的弹窗滚动锁定。
 *
 * 【为什么原来的锁不管用】
 * 本站全站跑 Lenis 平滑滚动（assets/yd-motion-v3.js 里创建，句柄 window.kwLenis）。
 * Lenis 会全局劫持滚轮事件，自己去驱动文档滚动 —— 所以 html/body 上的
 * overflow:hidden 对它毫无约束力。2026-08-26 在线上 /pages/sizing 实测：
 * html 与 body 的 computed overflow-y 都是 hidden，滚轮停在弹窗背板上，
 * 文档照样从 scrollY 184 滚到 681。
 *
 * 同一个原因还造成第二个症状：滚轮停在弹窗**内部**时，事件被 Lenis 吞掉，
 * 弹窗自己的滚动容器根本收不到，用户看到的是「在弹窗里滚，滚的是页面」。
 *
 * 【三件套，缺一不可（每一条都在线上实测过）】
 * 1) kwLenis.stop()          —— 让 Lenis 停手。单独用不够：Lenis 停了以后原生滚动会接管，
 *                               实测背板仍能从 0 滚到 600。
 * 2) body position:fixed     —— 文档高度塌成一屏，原生滚动也没有余量可滚。
 * 3) data-lenis-prevent      —— 打在 <dialog> 本体上。Lenis 从事件目标往上找这个属性，
 *                               命中就完全不介入，于是弹窗内部的滚动容器恢复原生滚动。
 *                               打在 dialog 上即可覆盖其全部后代，不必逐个面板改标记。
 * 三者齐备后实测：背板锁死不动，弹窗内面板滚满全程（scrollTop 0 → 79，可滚量正好 79）。
 *
 * 【两个必须的细节，删了就出 bug】
 * a) 解锁时必须先强制回流再还原滚动位置。去掉 position:fixed 的那一刻文档高度尚未恢复，
 *    最大滚动量仍是 0，此时调 scrollTo 会被钳到顶部 —— 实测就是这样把 684 丢成 0 的。
 * b) 用计数器而不是布尔量。弹窗可能叠加（图集弹窗里再开快速加购），
 *    布尔量会让内层关闭时把外层的锁一起解掉。
 *
 * 【覆盖面】
 * 直接给 HTMLDialogElement.prototype.showModal 打补丁，站内所有 <dialog> 自动生效 ——
 * 包含 Horizon 的 dialog.js / zoom-dialog.js 与本项目的 yd-sizing-modals.js，
 * 不必逐个组件接线，也不改动 Horizon 原生文件。
 * 非 <dialog> 的弹窗（yd-press-modal 用 hidden 属性 + 固定定位遮罩）自行调用
 * window.ydScrollLock.lock(el) / .unlock(el)。
 *
 * <details scroll-lock> 那一类（抽屉 / 搜索 / 筛选 / 快速加购）不进 top layer，
 * Horizon 自带的 html[scroll-lock]{overflow:hidden} 对它们有效，本文件不接管 ——
 * 但它们同样会被 Lenis 吞掉内部滚动，故在 lock 之外也给它们补 data-lenis-prevent。
 */
(function () {
  var depth = 0;
  var savedY = 0;
  var marked = [];

  function lenis() {
    return window.kwLenis || null;   // reduced-motion 下 yd-motion-v3 不创建 Lenis
  }

  function mark(el) {
    if (!el || el.hasAttribute('data-lenis-prevent')) return;
    el.setAttribute('data-lenis-prevent', '');
    marked.push(el);
  }

  function lock(scopeEl) {
    mark(scopeEl);

    depth += 1;
    if (depth > 1) return;

    var L = lenis();
    if (L && typeof L.stop === 'function') L.stop();

    savedY = window.scrollY || document.documentElement.scrollTop || 0;

    // 滚动条消失会让内容横向跳一下；有实体滚动条时用等宽 padding 补偿。
    // macOS 的覆盖式滚动条宽度为 0，这里自然不补。
    var barWidth = window.innerWidth - document.documentElement.clientWidth;
    if (barWidth > 0) document.body.style.paddingRight = barWidth + 'px';

    var s = document.body.style;
    s.position = 'fixed';
    s.top = -savedY + 'px';
    s.left = '0';
    s.right = '0';
    s.width = '100%';
  }

  function unlock() {
    if (depth === 0) return;
    depth -= 1;
    if (depth > 0) return;

    var s = document.body.style;
    s.position = '';
    s.top = '';
    s.left = '';
    s.right = '';
    s.width = '';
    s.paddingRight = '';

    // 强制回流：不读这一下，下面的 scrollTo 会因为文档高度尚未恢复而被钳到 0（见文件头注释 a）
    void document.body.offsetHeight;

    // 还原必须是**瞬时**的。base.css 给 html 设了 scroll-behavior: smooth，
    // 直接 scrollTo 会让页面从顶部平滑滑回原位 —— 用户看到的就是
    // 「关掉弹窗后页面从顶上滑下来」。关弹窗不是一次导航，不该有滚动动画。
    var root = document.documentElement;
    var prevBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    window.scrollTo(0, savedY);
    root.style.scrollBehavior = prevBehavior;

    var L = lenis();
    // Lenis 维护自己的一份滚动位置。不同步这一下，它会以为当前还在 0，
    // 下一次滚轮就把页面从 0 重新算起 —— 同样表现为「从顶部滑下来」。
    if (L && typeof L.scrollTo === 'function') L.scrollTo(savedY, { immediate: true });
    if (L && typeof L.start === 'function') L.start();

    while (marked.length) marked.pop().removeAttribute('data-lenis-prevent');
  }

  window.ydScrollLock = {
    lock: lock,
    unlock: unlock,
    isLocked: function () { return depth > 0; },
  };

  // 所有 <dialog>.showModal() 自动接管
  if (typeof HTMLDialogElement !== 'undefined' && HTMLDialogElement.prototype.showModal) {
    var nativeShowModal = HTMLDialogElement.prototype.showModal;
    HTMLDialogElement.prototype.showModal = function () {
      var wasOpen = this.open;
      var result = nativeShowModal.apply(this, arguments);
      // 已经开着时再调一次不重复加锁，否则只会解一次、锁会漏解
      if (!wasOpen) {
        lock(this);
        watchUntilClosed(this);
      }
      return result;
    };
  }

  /**
   * 盯住 open 属性直到弹窗关闭，然后解锁。
   *
   * ⚠️ 这里**不能**用 close 事件，虽然规范说 close() 应当派发它。
   * 2026-08-26 在线上 /pages/sizing 实测：dialog.close() 确实把 open 从 true 变成了 false，
   * 但直接挂在该 dialog 上的 close 监听器一次都没被调用（cancel 同样没有）。
   * 若把解锁挂在 close 事件上，弹窗一关页面就永久卡在 body{position:fixed} —— 整页动不了。
   *
   * 改盯 open 属性后，三条关闭路径实测全部命中：
   * close() / 点背板 / 真实 Esc 按键。
   */
  function watchUntilClosed(dialog) {
    if (typeof MutationObserver !== 'function') return;
    var mo = new MutationObserver(function () {
      if (dialog.open) return;
      mo.disconnect();
      unlock();
    });
    mo.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  // <details scroll-lock> 抽屉：Horizon 的 overflow 锁对它有效，但 Lenis 仍会吞掉
  // 抽屉内部的滚动，故这里只补 data-lenis-prevent，不介入锁本身。
  document.addEventListener('toggle', function (event) {
    var el = event.target;
    if (!(el instanceof HTMLDetailsElement) || !el.hasAttribute('scroll-lock')) return;
    if (el.open) mark(el);
    else if (el.hasAttribute('data-lenis-prevent')) {
      el.removeAttribute('data-lenis-prevent');
      var i = marked.indexOf(el);
      if (i > -1) marked.splice(i, 1);
    }
  }, true);
})();
